import { defineStore } from "pinia";

import type { StepType, StrapiProductType, ProductType } from "@/types";

interface State {
  activationSteps: StepType[];
  activeStep: number;
  manualAddress: boolean;
  products: ProductType[];
  simNumber: string;
  proceedSimResponse: any | null;
  shippingAddressData: {
    address: any;
    listing: string;
    selectedAddressId: number | null;
  } | null;
  yourNumberData: {
    serviceNumber: string;
    portedNumber: string;
    accountType: string;
    dateOfBirth: string;
    securityPin: string;
    currentProvider: string;
    previousAccountId: string;
    // OnePass primary contact number fields
    isPrimaryContactNumber: string;
    primaryContactNumber: string;
    primaryContactOtp: string;
  } | null;
  idVerificationData: any | null;
  verifyBackendResponse: any | null;
  // Snapshot of whether the user's OnePass mobile was already verified when the
  // activation journey started. Used to keep the "primary contact for OnePass
  // membership" section visibility stable — an in-flow OTP verification must not
  // hide the section for the rest of the same journey (IGNITE-66 edge case).
  mobileVerifiedAtJourneyStart: boolean | null;
}

const initialActivationSteps: StepType[] = [
  {
    slug: "your-sim",
    title: "Your Sim",
    completed: false,
    innerStep: 0,
  },
  {
    slug: "shipping-address",
    title: "Your address",
    completed: false,
  },
  {
    slug: "your-number",
    title: "Your number",
    completed: false,
  },
  {
    slug: "id-verification",
    title: "Id verification",
    completed: false,
  },
  {
    slug: "payment-method",
    title: "Your payment",
    completed: false,
  },
  {
    slug: "review",
    title: "Review",
    completed: false,
    submitStep: true,
  },
];

export const useActivationStore = defineStore({
  id: "activationStore",
  state: (): State => ({
    activationSteps: initialActivationSteps,
    activeStep: 0,
    manualAddress: false,
    products: [],
    simNumber: "",
    proceedSimResponse: null,
    shippingAddressData: null,
    yourNumberData: null,
    idVerificationData: null,
    verifyBackendResponse: null,
    mobileVerifiedAtJourneyStart: null,
  }),
  persist: process.client
    ? ({
        storage: localStorage,
        paths: [
          "activeStep",
          "simNumber",
          "shippingAddressData",
          // NOTE: "yourNumberData" is intentionally NOT persisted (IGNITE-66).
          // The port selection is retained account-based from the backend
          // pending-activation state (proceedSimResponse), not localStorage.
          "idVerificationData",
          "proceedSimResponse",
          "verifyBackendResponse",
          "activationSteps",
        ],
      } as any)
    : false,
  actions: {
    clearActivationSteps() {
      this.activationSteps = initialActivationSteps;
      this.activeStep = 0;
      this.manualAddress = false;
      this.products = [];
      this.simNumber = "";
      this.proceedSimResponse = null;
      this.shippingAddressData = null;
      this.yourNumberData = null;
      this.idVerificationData = null;
      this.verifyBackendResponse = null;
      this.mobileVerifiedAtJourneyStart = null;
    },
    clearSensitiveData() {
      // Clear only sensitive data while preserving form progress
      this.idVerificationData = null;
      this.verifyBackendResponse = null;
      // Keep simNumber, shippingAddressData, and step progress for UX
    },
    clearOnCompletion() {
      // Complete cleanup after successful activation
      if (process.client) {
        // Clear localStorage items
        localStorage.removeItem("activation-selectedAddressId");
        localStorage.removeItem("activation-deliveryMethod");
      }
      // Reset all state
      this.activationSteps = initialActivationSteps;
      this.activeStep = 0;
      this.manualAddress = false;
      this.products = [];
      this.simNumber = "";
      this.proceedSimResponse = null;
      this.shippingAddressData = null;
      this.yourNumberData = null;
      this.idVerificationData = null;
      this.verifyBackendResponse = null;
      this.mobileVerifiedAtJourneyStart = null;
    },
    markStepCompleted(stepIndex: number) {
      if (this.activationSteps[stepIndex]) {
        this.activationSteps[stepIndex].completed = true;
      }
    },
    setActiveStep(stepIndex: number) {
      if (stepIndex >= 0 && stepIndex < this.activationSteps.length) {
        this.activeStep = stepIndex;
      }
    },
    updateProducts(products: ProductType[] | null) {
      if (!products) return;
      this.products = products;
    },
    updateProductsWithStrapi(strapiProducts: StrapiProductType[] | null) {
      if (!strapiProducts) return;
      strapiProducts.forEach((strapiProduct) => {
        const productIndex = this.products.findIndex(
          (product) => product.code === strapiProduct.code
        );
        if (productIndex === -1) return;
        this.products[productIndex].strapiData = strapiProduct;
      });
    },
    setStepInnerStep(stepIndex: number, innerStep: number) {
      if (this.activationSteps[stepIndex]) {
        this.activationSteps[stepIndex].innerStep = innerStep;
      }
    },
    setSimNumber(number: string) {
      this.simNumber = number;
    },
    setProceedSimResponse(response: any) {
      this.proceedSimResponse = response;
    },
    setShippingAddressData(data: any) {
      console.log("setShippingAddressData", data);
      this.shippingAddressData = data;
    },
    setYourNumberData(data: any) {
      console.log("setYourNumberData", data);
      this.yourNumberData = data;
    },
    setIdVerificationData(data: any) {
      this.idVerificationData = data;
    },
    setVerifyBackendResponse(data: any) {
      this.verifyBackendResponse = data;
    },
    setMobileVerifiedAtJourneyStart(value: boolean) {
      this.mobileVerifiedAtJourneyStart = value;
    },
  },
  getters: {},
});
